/**
 * PAI entrypoint.
 *
 * Mounts the full CIP application shell (providers, router, course pages, …)
 * and swaps in PAI-specific overrides: the splash/loading/error pages, and
 * PAI's own tool catalog at `/:courseId/tools/*` ({@link PaiToolRoutes}). No
 * CIP source files are copied, forked, or patched.
 *
 * Everything around the render — the stale-chunk guard, the production console
 * interception and error forwarding, the service workers, the slow cold-load
 * timer — comes from CIP's shared {@link bootstrapCipApp}, so this entrypoint
 * cannot drift away from the webapp's again
 * ([#3696](https://github.com/CodeinPlace/cip/issues/3696)).
 *
 * To add further overrides in the future, extend the `components` argument —
 * see {@link AppOverrides} in CIP's AppOverridesContext.
 */

// CIP firebase initialisation must run before anything else uses Firebase.
import '@cip/webapp/firebaseApp';
// Monaco's loader setup (monaco/configureLoader) is intentionally NOT imported
// here: it statically imports all of monaco-editor, which would drag
// vendor-monaco into the entry chunk in front of the landing page. The lazily
// loaded course subtree imports it instead (see CourseRoutes in
// `@cip/webapp`), so the loader is configured before any editor mounts. See
// [#3616](https://github.com/CodeinPlace/cip/issues/3616).

// Register Jost (the self-hosted Futura revival) app-wide so PAI's theme can
// use it for headings on every page, not just where slides load it. KaTeX_Main
// (PAI's body font) is already registered app-wide via KaTeX's stylesheet.
import '@fontsource/jost/400.css'; // Regular
import '@fontsource/jost/700.css'; // Bold

// `bootstrapCipApp` mounts the full CIP application shell: all providers + the
// router. Importing it here bundles CIP's code into the PAI build — no sources
// are copied or mutated; CIP's files remain the single source of truth.
//
// The Math IDE and slide editor are deliberately NOT imported here. PAI used
// to import them eagerly and inject them as `MathIdeRoute` / `SlidesRoutes`
// overrides, but that pulled Monaco + the whole slides subsystem into the
// entry chunk, in front of the landing page (see
// [#3616](https://github.com/CodeinPlace/cip/issues/3616)). CIP's defaults
// load both with `React.lazy` behind existing `Suspense` fallbacks (see
// CourseRoutes), which keeps them out of the critical path until a student
// actually opens one.
import {lazy} from 'react';
import {bootstrapCipApp} from '@cip/webapp/bootstrapCipApp';
import {PaiCourseHomePage} from './PaiCourseHomePage';
import {PaiErrorPage} from './PaiErrorPage';
import {PaiLoadingPage} from './PaiLoadingPage';
import {PaiSplashPage} from './PaiSplashPage';
import {PaiTeamPage} from './PaiTeamPage';
import {PAI_COURSE_THEME} from './paiTheme';
import {POST_TOY_OPTIONS} from './postToys/postToyOptions';
import {PaiToolRoutes} from './PaiToolRoutes';
import {
  ExpeditionRoadmapView,
  PlainFlatRoadmapView,
} from './roadmap/ExpeditionRoadmapView';

bootstrapCipApp({
  // Distinct from the webapp's 'cip-webapp' so PAI's entries stay
  // identifiable in the shared error logs.
  webApp: 'cip-pai',
  components: {
    SplashPage: PaiSplashPage,
    LoadingPage: PaiLoadingPage,
    ErrorPage: PaiErrorPage,
    CourseHomePage: PaiCourseHomePage,
    // Lazy so the calendar and mascots stay out of the entry chunk.
    TeachingHomePage: lazy(() =>
      import('./teacherHome/PaiTeachingHome').then(m => ({
        default: m.PaiTeachingHome,
      })),
    ),
    TeamPage: PaiTeamPage,
    // Lazy so the Toolbox's content and math stay out of the entry chunk.
    ToolboxPage: lazy(() =>
      import('./toolbox/PaiToolboxPage').then(m => ({
        default: m.PaiToolboxPage,
      })),
    ),
    // The class research hub; lazy so its live editor stays out of the entry
    // chunk.
    ResearchHubPage: lazy(() =>
      import('./researchHub/ResearchHubPage').then(m => ({
        default: m.ResearchHubPage,
      })),
    ),
    // The T-shirt contest; lazy so its forms and mascots stay out of the
    // entry chunk.
    TshirtContestPage: lazy(() =>
      import('./tshirt/PaiTshirtContestPage').then(m => ({
        default: m.PaiTshirtContestPage,
      })),
    ),
    // The lesson outline's Tools list; lazy for the same reason.
    LessonOutlineExtra: lazy(() =>
      import('./toolbox/LessonOutlineTools').then(m => ({
        default: m.LessonOutlineTools,
      })),
    ),
    // The Calculator link under a lesson question; lazy for the same reason.
    ConceptQuestionExtra: lazy(() =>
      import('./toolbox/ConceptQuestionCalculatorLink').then(m => ({
        default: m.ConceptQuestionCalculatorLink,
      })),
    ),
    ToolRoutes: PaiToolRoutes,
    appName: 'Probability for Artificial Intelligence',
    // Bumping this on global/webapp prompts only PAI tabs to reload;
    // bumping version there prompts Code in Place's too.
    versionField: 'paiVersion',
    courseTheme: PAI_COURSE_THEME,
    // Appearance is fixed for everyone: no dark theme is supplied, and the
    // sidebar, forum, and roadmap choices are not offered, so these defaults
    // apply whatever a user stored earlier.
    defaultSidebarStyle: 'flat',
    defaultRoadmapStyle: 'expedition',
    roadmapViews: [
      {id: 'expedition', label: 'Expedition', component: ExpeditionRoadmapView},
      {
        id: 'plain-flat',
        label: 'Plain · flat',
        component: PlainFlatRoadmapView,
      },
    ],
    // The roadmap editor's Look & feel page, loaded only when opened so the
    // maps, mascots and widget catalogue stay out of the entry bundle.
    RoadmapLookAndFeelEditor: lazy(() =>
      import('./roadmap/lookAndFeel/LookAndFeelEditor').then(m => ({
        default: m.LookAndFeelEditor,
      })),
    ),
    // Toys staff put in forum posts; the toys themselves load only for a post
    // that holds one.
    postToyOptions: POST_TOY_OPTIONS,
    PostToy: lazy(() =>
      import('./postToys/PaiPostToy').then(m => ({default: m.PaiPostToy})),
    ),
    // Ollie coaches the puzzle pages; lazy so the mascot stays out of the
    // entry bundle.
    PuzzleCoach: lazy(() =>
      import('./puzzles/PaiPuzzleCoach').then(m => ({
        default: m.PaiPuzzleCoach,
      })),
    ),
    // PAI has no separate course-selection landing, so the exit/back
    // button's course-home fallback should go to the index route rather
    // than `/<courseId>`.
    defaultHomeUrl: '/',
    // PAI has no Karel or Python reader, so the learn page's Readers column
    // would only link to empty pages.
    hidesLearnReaders: true,
    // PAI's section leaders plan each week from the teacher handbook.
    sendsSectionGuideToHandbook: true,
    // The PAI learn page is just the lesson list; the CIP welcome text slot
    // above it would otherwise render as an empty editor for admins.
    hidesLearnIntro: true,
    learnBookLink: {
      label: 'Course book: Probability for AI',
      href: 'https://probabilitycoders.stanford.edu/pai',
    },
    // PAI event names are long; wrapping keeps them readable.
    wrapsStudentCalendarEventNames: true,
    widensStudentHome: true,
    // The SL home's cohort map and line, in place of the CIP map. Lazy so
    // the SL page's styles stay out of the entry chunk.
    CourseCardMap: lazy(() =>
      import('./teacherHome/CohortSummary').then(m => ({
        default: m.StudentCohortSummary,
      })),
    ),
  },
});
